/**
 * StoreFloor + Clienteling — the Store-Ops flagship views,
 * ported faithfully from the 3-in-1 (spec §3.1–3.2). Data: getStoreFloor
 * (seed-served, marker convention). Assign/notes/booking/publish are awaited ops.
 * i18n (2026-08-05): every string on this screen resolves through the catalog —
 * no template literals, no English fallbacks baked into layout, and every
 * dynamic key (category/tier/reason) goes through the guarded helpers in
 * storeops-i18n so a drifting payload can never echo a raw key again.
 */
import { useEffect, useMemo, useState } from "react";
import { Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useWfResource } from "../workforce/useWorkforce";
import { WfPending } from "../workforce/wf-shared";
import { getStoreFloor, autoMatchSessions, publishStoreRoster, type ServiceCategoryCode, type SoFloor, type SoMatch, type SoZone } from "./so-api";
import { useActiveStore } from "./useStores";
import { GOLD, GOLD_SOFT, INK, BrandHeader, Explainer, SoKpi, TierBadge, MatchMeter, ClientDrawer, BookingWizard, soCard, cardHead, goldBtn, ghostBtn, initialsOf } from "./so-kit";
import { useT, t as i18nT } from "../../lib/i18n";
import { formatClock, formatFloorDate, formatMoneyK, formatSpend, formatWeekday, renderMatchReason, renderNeedLine, serviceCategoryLabel } from "./storeops-i18n";

const ZONE_STATE: Record<SoZone["state"], { labelKey: string; fg: string; bg: string }> = {
  ok: { labelKey: "storeops.storeFloor.covered", fg: T.green1, bg: alpha(T.green1, 12) },
  under: { labelKey: "storeops.storeFloor.text1Short", fg: T.orange1, bg: alpha(T.orange1, 12) },
  risk: { labelKey: "storeops.storeFloor.specialistGap", fg: T.red1, bg: alpha(T.red1, 10) },
};
const GAP_TONE: Record<string, string> = { risk: T.red1, under: T.orange1, info: T.tDarkBlue };

function useFloor() {
  const { storeId } = useActiveStore();
  const res = useWfResource<SoFloor>(`sofloor:${storeId}`, () => getStoreFloor(storeId));
  return { ...res, storeId };
}

function ClientelingCard({ floor, onOpen, onChanged }: { floor: SoFloor; onOpen: (m: SoMatch) => void; onChanged: (m: SoMatch) => void }) {
  const t = useT();
  const gapCount = floor.matches.filter((m) => m.state === "gap").length;
  return (
    <div style={{ ...soCard, overflow: "hidden" }}>
      <div style={cardHead}>
        <span style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 15, fontWeight: 700, color: T.n1 }}><span style={{ color: GOLD, display: "inline-flex" }}><Icon.star size={17} /></span>{t("storeops.storeFloor.clientelingTodaySBookedClients", "Clienteling — today's booked clients")}</span>
        <span style={{ fontSize: 11.5, color: T.n2, background: T.n6, borderRadius: 999, padding: "3px 10px" }}>{t("storeops.storeFloor.sessionCount.icu", "{count} sessions · {gaps} needs a match", { count: floor.matches.length, gaps: gapCount })}</span>
      </div>
      {floor.matches.map((m, i) => {
        const reason = renderMatchReason(m.why, t);
        return (
        <div key={m.id} data-testid="sofloor-match-row" data-id={m.id} onClick={() => onOpen(m)}
          style={{ display: "flex", flexDirection: "column", gap: 10, padding: "16px 20px", borderTop: i ? `1px solid ${T.n5}` : "none", background: m.state === "gap" ? alpha(T.red1, 6) : "transparent", cursor: "pointer" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <span style={{ fontFamily: T.fontMono, fontSize: 13, fontWeight: 700, color: T.n1, width: 46, flexShrink: 0 }}>{m.time}</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 180 }}>
              <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{m.client}</span>
                <TierBadge tierCode={m.tierCode} />
                <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.soft }}>{formatSpend(m.spend)}</span>
              </span>
              <span style={{ fontSize: 12.5, color: T.n2 }}>{renderNeedLine(m.needCode, m.detail.langs, t)}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", minWidth: 150 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: m.state === "gap" ? T.red1 : T.n1 }}>{m.advisor ?? t("storeops.storeFloor.unassigned", "— unassigned —")}</span>
              <MatchMeter score={m.match} />
              <span style={{ display: "flex", gap: 3, fontSize: 11, fontWeight: 600, color: GOLD, alignItems: "center" }}>{t("storeops.storeFloor.view360", "View 360")}<Icon.chev dir="right" size={11} /></span>
            </div>
          </div>
          {reason ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", paddingLeft: 58 }}>
              <span style={{ color: T.soft, marginTop: 2, flexShrink: 0, display: "inline-flex" }}><Icon.help size={13} /></span>
              <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, flex: 1 }}>{reason}</span>
            </div>
          ) : null}
        </div>
        );
      })}
    </div>
  );
}

function ZonesCard({ zones }: { zones: SoZone[] }) {
  const t = useT();
  return (
    <div style={{ ...soCard, padding: "18px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{t("storeops.storeFloor.salesFloorCoverageByZone", "Sales floor — coverage by zone")}</span>
        <span style={{ fontSize: 12, color: T.soft }}>{t("storeops.storeFloor.nowAt.icu", "Now · {time}", { time: formatClock() })}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {zones.map((z) => {
          const st = ZONE_STATE[z.state] ?? { labelKey: "storeops.storeFloor.stateUnknown", fg: T.n2, bg: T.n6 };
          return (
            <div key={z.id} data-testid="sofloor-zone-row" data-id={z.id} style={{ display: "flex", gap: 14, alignItems: "center", padding: "12px 14px", borderRadius: 8, border: `1px solid ${T.n5}`, background: z.state === "risk" ? alpha(T.red1, 6) : T.white }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{z.name}</span>
                <span style={{ fontSize: 11.5, color: T.soft }}>{t("storeops.storeFloor.zoneCoverageLine.icu", "Needs {category} · {appts} appts · {footfall} footfall/hr", { category: serviceCategoryLabel(z.serviceCategoryCode, t), appts: z.appts, footfall: z.footfall })}</span>
              </div>
              <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, minWidth: 52 }}>
                <span style={{ fontFamily: T.fontMono, fontSize: 15, fontWeight: 700, color: z.state === "ok" ? T.n1 : st.fg }}>{z.staffed}/{z.need}</span>
                <span style={{ fontSize: 10, color: T.soft }}>{t("storeops.storeFloor.onFloor", "on floor")}</span>
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: st.bg, color: st.fg, whiteSpace: "nowrap", minWidth: 96, textAlign: "center" }}>{t(st.labelKey)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DemandCard({ floor }: { floor: SoFloor }) {
  const t = useT();
  return (
    <div style={{ ...soCard, padding: "18px 20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 14 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("storeops.storeFloor.blendedDemandDay.icu", "Blended demand — {weekday}", { weekday: formatWeekday(floor.dateIso) })}</span>
        <span style={{ fontSize: 11.5, color: T.soft }}>{t("storeops.storeFloor.walkInFootfallBookedAppointmentsPerHour", "Walk-in footfall + booked appointments per hour")}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${floor.hours.length}, 1fr)`, gap: 6, alignItems: "end", height: 130 }}>
        {floor.hours.map((h, i) => {
          const footH = Math.round((h.foot / floor.footMax) * 100);
          const apptH = Math.round((h.appt / 8) * 46);
          return (
            <div key={i} title={i18nT("storeops.storeFloor.hH00HFootFootfallHApptAppts.icu", "{value0}:00 — {value1} footfall · {value2} appts", { value0: h.h, value1: h.foot, value2: h.appt })} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
              <span style={{ width: "100%", height: apptH, background: GOLD, borderRadius: "4px 4px 0 0", display: "block" }} />
              <span style={{ width: "100%", height: `${footH}%`, background: T.p2, borderRadius: apptH ? 0 : "4px 4px 0 0", display: "block" }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${floor.hours.length}, 1fr)`, gap: 6, marginTop: 6 }}>
        {floor.hours.map((h, i) => <span key={i} style={{ fontFamily: T.fontMono, fontSize: 10, textAlign: "center", color: T.soft }}>{h.h}</span>)}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", gap: 6, fontSize: 11, color: T.soft, alignItems: "center" }}><span style={{ width: 12, height: 12, borderRadius: 3, background: T.p2 }} /> {t("storeops.storeFloor.walkInFootfall", "Walk-in footfall")}</span>
        <span style={{ display: "inline-flex", gap: 6, fontSize: 11, color: T.soft, alignItems: "center" }}><span style={{ width: 12, height: 12, borderRadius: 3, background: GOLD }} /> {t("storeops.storeFloor.bookedAppointments", "Booked appointments")}</span>
      </div>
    </div>
  );
}

function GapsCard({ floor }: { floor: SoFloor }) {
  const t = useT();
  return (
    <div style={{ ...soCard, padding: "18px 20px" }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: T.n1, display: "block", marginBottom: 14 }}>{t("storeops.storeFloor.specialistGaps", "Specialist gaps")}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {floor.gaps.map((g, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: GAP_TONE[g.sev] ?? T.soft, marginTop: 5, flexShrink: 0 }} />
            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{g.head}</span>
              <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{g.body}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BenchCard({ floor }: { floor: SoFloor }) {
  const t = useT();
  return (
    <div style={{ ...soCard, padding: "18px 20px" }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: T.n1, display: "block", marginBottom: 14 }}>{t("storeops.storeFloor.specialistsOnToday", "Specialists on today")}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {floor.bench.map((b, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, background: GOLD_SOFT, color: GOLD, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700 }}>{initialsOf(b.name)}</span>
            <span style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{b.name}</span>
              <span style={{ fontSize: 11.5, color: T.soft }}>{b.role} · {b.langs}</span>
            </span>
            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, minWidth: 60 }}>
              <span style={{ width: 46, height: 5, borderRadius: 5, background: T.n6, overflow: "hidden", display: "inline-block" }}>
                <span style={{ display: "block", width: `${b.util}%`, height: "100%", borderRadius: 5, background: b.util > 88 ? T.orange1 : GOLD }} />
              </span>
              <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.soft }}>{t("storeops.storeFloor.benchUtil.icu", "{pct}% booked", { pct: b.util })}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiRow({ floor }: { floor: SoFloor }) {
  const t = useT();
  const k = floor.kpis;
  const gapCount = floor.matches.filter((m) => m.state === "gap").length;
  const peakHour = floor.hours.length ? floor.hours.reduce((a, b) => (b.foot > a.foot ? b : a)).h : "";
  // Which categories are short — derived from the SERVED zone states and the
  // unmatched sessions, so the sub-line never carries hand-written English.
  const shortCategories = useMemo(() => {
    const codes: ServiceCategoryCode[] = [];
    floor.zones.forEach((z) => { if (z.state !== "ok" && !codes.includes(z.serviceCategoryCode)) codes.push(z.serviceCategoryCode); });
    floor.matches.forEach((m) => { if (m.state === "gap" && !codes.includes(m.needCode)) codes.push(m.needCode); });
    return codes;
  }, [floor]);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
      <SoKpi of={k} label={t("storeops.storeFloor.bookedSessions", "Booked sessions")} value={k.booked} sub={t("storeops.storeFloor.bookedSplit.icu", "{vip} VIP · {priv} private", { vip: k.bookedVip, priv: k.bookedPrivate })} accent={GOLD} />
      <SoKpi of={k} label={t("storeops.storeFloor.expectedFootfall", "Expected footfall")} value={k.footfall} sub={peakHour ? t("storeops.storeFloor.peakHour.icu", "peak {hour}:00", { hour: peakHour }) : undefined} />
      <SoKpi of={k} label={t("storeops.storeFloor.clientelingMatch", "Clienteling match")} value={t("storeops.soKit.text", "{score}%", { score: k.match })} sub={gapCount ? t("storeops.storeFloor.unmatchedCount.icu", "{count} unmatched", { count: gapCount }) : t("storeops.storeFloor.matchStrong", "strong")} accent={T.green1} />
      <SoKpi of={k} label={t("storeops.storeFloor.specialistGaps", "Specialist gaps")} value={k.gaps} sub={k.gaps && shortCategories.length ? shortCategories.map((c) => serviceCategoryLabel(c, t)).join(" · ") : t("storeops.storeFloor.fullyCovered", "fully covered")} accent={k.gaps ? T.red1 : T.green1} />
      <SoKpi of={k} label={t("storeops.storeFloor.monthToDate", "Month to date")} value={formatMoneyK(k.mtdK, floor.currencyCode)} sub={t("storeops.storeFloor.attainOfTarget.icu", "{pct}% of target", { pct: k.attain })} accent={T.orange1} />
    </div>
  );
}

/** Shared shell for the day + clients views. */
function FloorShell({ view, rail }: { view: "day" | "clients"; rail: string }) {
  const t = useT();
  const nav = useNav();
  const { data: floor, loading, error, reload } = useFloor();
  const [openClient, setOpenClient] = useState<SoMatch | null>(null);
  const [booking, setBooking] = useState<{ name?: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const canWrite = can("clienteling.write");
  // Keep the open drawer in sync with server-confirmed updates.
  const onChanged = (m: SoMatch) => { setOpenClient((cur) => (cur && cur.id === m.id ? m : cur)); reload(); };
  useEffect(() => { setOpenClient(null); }, [floor?.storeId]);

  const headerActions = floor ? (
    <>
      {/* ui-raw: control on the storeops dark header, filled with the vertical GOLD accent — needs the on-brand treatment (capability 3) and the gold is not a jrni token */}
      {canWrite ? <button data-testid="sofloor-book-open" style={ghostBtn} onClick={() => setBooking({})}><Icon.plus size={13} /> {t("storeops.storeFloor.book", "Book")}</button> : null}
      {canWrite ? (
        /* ui-raw: control on the storeops dark header, filled with the vertical GOLD accent — needs the on-brand treatment (capability 3) and the gold is not a jrni token */
        <button data-testid="sofloor-automatch-action" style={ghostBtn} disabled={busy !== null} onClick={async () => {
          setBusy("match");
          try {
            const r = await autoMatchSessions(floor.storeId);
            toast({ tone: "success", title: t("storeops.storeFloor.autoMatchingBookedSessions", "Auto-matching booked sessions"), subtitle: t("storeops.storeFloor.bestSpecialistProposedForRMatchedUnmatchedClientRMatched1S.icu", "Best specialist proposed for {count, plural, one {# unmatched client} other {# unmatched clients}}", { count: r.matched }) });
            nav.go("storeops.clients");
            reload();
          } catch (e) { toast({ tone: "danger", title: t("storeops.storeFloor.autoMatchFailed", "Auto-match failed"), subtitle: e instanceof Error ? e.message : String(e) }); }
          finally { setBusy(null); }
        }}><Icon.refresh size={13} /> {busy === "match" ? t("storeops.storeFloor.matching", "Matching…") : t("storeops.storeFloor.autoMatch", "Auto-match")}</button>
      ) : null}
      {can("storeops.manage") ? (
        /* ui-raw: control on the storeops dark header, filled with the vertical GOLD accent — needs the on-brand treatment (capability 3) and the gold is not a jrni token */
        <button data-testid="sofloor-publish-action" style={goldBtn} disabled={busy !== null} onClick={async () => {
          setBusy("publish");
          try {
            const r = await publishStoreRoster(floor.storeId);
            toast({ tone: "success", title: t("storeops.storeFloor.rosterPublished", "Roster published"), subtitle: i18nT("storeops.storeFloor.todaySFloorPlanSentToAllRNotifiedSpecialists.icu", "Today's floor plan sent to all {value0} specialists", { value0: r.notified }) });
          } catch (e) { toast({ tone: "danger", title: t("storeops.storeFloor.publishFailed", "Publish failed"), subtitle: e instanceof Error ? e.message : String(e) }); }
          finally { setBusy(null); }
        }}>{busy === "publish" ? t("storeops.storeFloor.publishing", "Publishing…") : t("storeops.storeFloor.publishRoster", "Publish roster")}</button>
      ) : null}
    </>
  ) : null;

  const floorDate = floor ? formatFloorDate(floor.dateIso) : "";
  return (
    <Screen rail={rail}>
      <div style={{ flex: 1, overflow: "auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
        <BrandHeader
          viewLabel={t("shell.access.storeOperations", "Store operations")}
          title={floor ? floor.storeName + (floor.flag ? t("storeops.storeFloor.flagship", " · Flagship") : "") : "…"}
          sub={floor
            ? (view === "clients"
              ? t("storeops.storeFloor.subClients.icu", "{date} · {count} booked sessions · {vip} VIP · {priv} private", { date: floorDate, count: floor.kpis.booked, vip: floor.kpis.bookedVip, priv: floor.kpis.bookedPrivate })
              : t("storeops.storeFloor.subDay.icu", "{date} · {count} specialists on · store open {hours}", { date: floorDate, count: floor.specialistsOn, hours: floor.openHours }))
            : ""}
          actions={headerActions}
        />
        <DataSourceStrip screen="sofloor" ops={["listStores", "getStoreFloor", "assignClientMatch", "addClientNote", "bookClientelingSession", "autoMatchSessions", "publishStoreRoster"]} />
        <div style={{ padding: "20px 24px 48px", display: "flex", flexDirection: "column", gap: 18 }}>
          {error ? <WfPending screen="sofloor" opName="getStoreFloor" error={error} onRetry={reload} />
            : loading || !floor ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("storeops.storeFloor.loadingTheFloor", "Loading the floor…")}</div>
            : (
              <>
                <Explainer>{t("storeops.storeFloor.sameSchedulingEngineRetailShapeDemandIs", "Same scheduling engine, retail shape: demand is")}{" "}<b>{t("storeops.storeFloor.blendedFootfallBookedClienteling", "blended footfall + booked clienteling")}</b>{t("storeops.storeFloor.staffingIsBy", ", staffing is by")}{" "}<b>{t("storeops.storeFloor.salesFloorZone", "sales-floor zone")}</b>{t("storeops.storeFloor.andAdvisorsAreMatchedOn", ", and advisors are matched on")}{" "}<b>{t("storeops.storeFloor.categoryExpertiseLanguageAndTheClientBook", "category expertise, language, and the client book")}</b> {t("storeops.storeFloor.theyHoldNotASingleGenericSkill", "they hold — not a single generic skill.")}</Explainer>
                <KpiRow floor={floor} />
                {view === "clients" ? (
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 18, alignItems: "start" }}>
                    <ClientelingCard floor={floor} onOpen={setOpenClient} onChanged={onChanged} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}><BenchCard floor={floor} /><GapsCard floor={floor} /></div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 360px", gap: 18, alignItems: "start" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
                      <ClientelingCard floor={floor} onOpen={setOpenClient} onChanged={onChanged} />
                      <ZonesCard zones={floor.zones} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                      <DemandCard floor={floor} />
                      <GapsCard floor={floor} />
                      <BenchCard floor={floor} />
                    </div>
                  </div>
                )}
              </>
            )}
        </div>
      </div>
      {openClient ? <ClientDrawer m={openClient} onClose={() => setOpenClient(null)} onChanged={onChanged} /> : null}
      {booking && floor ? <BookingWizard floor={floor} prefillName={booking.name} onClose={() => setBooking(null)} onBooked={() => reload()} /> : null}
    </Screen>
  );
}

export default function StoreFloor() { return <FloorShell view="day" rail="so-floor" />; }
export function Clienteling() { return <FloorShell view="clients" rail="so-clients" />; }
